import { useEffect, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 3;

export default function AddressSearch({ query, onQueryChange, onPlaceSelect, onLocateMe }) {
  const places = useMapsLibrary('places');
  const [text, setText] = useState(query);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  // token di sessione: raggruppa le richieste di digitazione + la selezione
  // in una sola sessione fatturata
  const sessionToken = useRef(null);
  // dopo una selezione il testo cambia da solo: niente nuova ricerca
  const skipFetch = useRef(false);

  // il testo torna in sync se la query viene azzerata da fuori
  useEffect(() => {
    if (query === '') return;
    setText(query);
  }, [query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (skipFetch.current) {
      skipFetch.current = false;
      return;
    }
    if (!places || text.trim().length < MIN_CHARS) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!sessionToken.current) {
        sessionToken.current = new places.AutocompleteSessionToken();
      }
      try {
        const { suggestions: res } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: text.trim(),
          sessionToken: sessionToken.current,
          language: 'it',
          region: 'it',
        });
        if (cancelled) return;
        setSuggestions(res.filter((s) => s.placePrediction));
        setHighlight(-1);
        setOpen(true);
      } catch (err) {
        if (!cancelled) {
          console.error('autocomplete fallito', err);
          setSuggestions([]);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [places, text]);

  const choose = async (suggestion) => {
    const prediction = suggestion.placePrediction;
    skipFetch.current = true;
    setText(prediction.text.text);
    setOpen(false);
    setSuggestions([]);
    // la selezione chiude la sessione: la prossima ricerca ne apre una nuova
    sessionToken.current = null;
    // l'indirizzo scelto non deve filtrare la lista negozi per testo
    onQueryChange('');

    const place = prediction.toPlace();
    await place.fetchFields({ fields: ['location', 'formattedAddress', 'viewport'] });
    if (!place.location) return;
    onPlaceSelect({
      lat: place.location.lat(),
      lng: place.location.lng(),
      viewport: place.viewport ? place.viewport.toJSON() : null,
      label: place.formattedAddress ?? prediction.text.text,
      ts: Date.now(),
    });
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      choose(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="search-wrap" ref={boxRef}>
      <div className="search-box">
        <input
          type="text"
          placeholder="inserisci città, indirizzo..."
          value={text}
          autoComplete="off"
          onChange={(e) => {
            setText(e.target.value);
            onQueryChange(e.target.value);
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="search-btn" aria-label="usa la mia posizione" onClick={onLocateMe}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="10" r="3" />
            <path d="M12 2a8 8 0 0 1 8 8c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 8-8z" />
          </svg>
        </button>
      </div>

      {open && suggestions.length > 0 && (
        <ul className="suggestions">
          {suggestions.map((s, i) => {
            const p = s.placePrediction;
            return (
              <li
                key={p.placeId}
                className={`suggestion ${i === highlight ? 'active' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(s)}
              >
                <span className="suggestion-main">{p.mainText?.text?.toLowerCase() ?? p.text.text.toLowerCase()}</span>
                {p.secondaryText?.text && <span className="suggestion-sub">{p.secondaryText.text.toLowerCase()}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
